import { useParams, useSearchParams, Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { articles, categories } from "@/mocks/forum";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import CategoryTabs from "@/components/base/CategoryTabs";
import ArticleCard from "@/components/base/ArticleCard";

const ARTICLES_PER_PAGE = 8;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";
  const [currentPage, setCurrentPage] = useState(1);

  const category = categories.find((c) => c.slug === slug);

  const filteredArticles = useMemo(() => {
    let result = articles;

    if (slug && category) {
      result = result.filter((a) => a.category === slug);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    return result;
  }, [slug, category, searchQuery]);

  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  const pageTitle = category
    ? `${category.name} - 楼凤论坛`
    : searchQuery
      ? `搜索：${searchQuery} - 楼凤论坛`
      : "分类浏览 - 楼凤论坛";

  const pageDescription = category
    ? `浏览楼凤论坛${category.name}板块的所有文章。${category.description}。`
    : "浏览楼凤论坛所有分类的文章，发现感兴趣的内容。";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageTitle,
    description: pageDescription,
    url: `https://www.loufengbbs.com/category/${slug || ""}`,
    isPartOf: {
      "@type": "WebSite",
      name: "楼凤论坛",
      url: "https://www.loufengbbs.com",
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-100">
          <Breadcrumb />

          <div className="w-full px-4 md:px-6 pb-6">
            <div className="flex flex-col gap-4">
              <div>
                {category ? (
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                      <i className={`${category.icon} text-primary-600 text-lg`}></i>
                    </span>
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                        {category.name}
                      </h1>
                      <p className="text-sm text-foreground-500 mt-1">
                        {category.description} · 共 {filteredArticles.length} 篇文章
                      </p>
                    </div>
                  </div>
                ) : searchQuery ? (
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                      搜索结果：{searchQuery}
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1">
                      找到 {filteredArticles.length} 篇相关文章
                    </p>
                  </div>
                ) : (
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                      分类浏览
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1">
                      浏览所有分类的文章
                    </p>
                  </div>
                )}
              </div>

              <CategoryTabs />
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          {paginatedArticles.length > 0 ? (
            <>
              <div className="flex flex-col gap-3">
                {paginatedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                        page === currentPage
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:bg-background-100"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-700 mb-2">暂无内容</h3>
              <p className="text-sm text-foreground-400 mb-6">
                {searchQuery ? "没有找到匹配的文章，试试其他关键词" : "该分类下暂时没有文章"}
              </p>
              <Link
                to="/"
                className="inline-flex px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}